import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Select, Modal, Input, Button, notification, Popconfirm, Dropdown, Tooltip } from 'antd'
import { EditFilled, DeleteFilled, CloseOutlined, SaveFilled } from '@ant-design/icons'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { ProfilesKeys } from '../../Constants/Configuration'
import {
  saveConfiguration,
  loadConfiguration,
  deleteConfiguration,
  renameConfiguration,
  setIsConfigurationModalOpen,
  setIsConfigurationEditModalOpen,
  setShowConfigurationElement,
} from '../../Slices/ConfigurationSlice'
import { handleErrorCatchLog, isFixedWorkspace, renderTranslation, stopPropagation } from '../../Helpers/GenericHelpers'
import { getTranslation } from '../../Services/StartupService'
import { loadLanguage } from '../../Slices/LanguageSlice'
import { populateTableWithArticles } from '../../Services/Kiosk/ArticleServices'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { checkDefaultTypeOfChannel, getConfigurationList } from '../../Helpers/ConfigurationHelper'
import { setTypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import { setChannelType } from 'Slices/Kiosk/PriceSlice'

const { Option } = Select

function ConfigurationDropdown() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const currentConfiguration = useAppSelector(state => state.present.configuration.currentConfiguration)
  const isConfigurationModalOpen = useAppSelector(state => state.present.configuration.isConfigurationModalOpen)
  const defaultProfileOptions = useAppSelector(state => state.present.configuration.defaultProfileOptions)
  const isConfigurationEditModalOpen = useAppSelector(state => state.present.configuration.isConfigurationEditModalOpen)
  const editTableDisable = useAppSelector(state => state.present.tableOptions.editTableDisable)
  const activeFilters = useAppSelector(state => state.present.tableOptions.activeFilters)
  const profileValue = useAppSelector(state => state.present.configuration.profileValue)
  const publicWorkspace = useAppSelector(state => state.present.configuration.publicWorkspace)
  const profileWorkspaces = useAppSelector(state => state.present.configuration.profileWorkspaces)
  const userValue = useAppSelector(state => state.present.configuration.userValue)
  const serverConfig = useAppSelector(state => state.present.article.serverConfig)
  const isOmnichannelAndPricesVisibile = useAppSelector(
    state => state.present.configuration.isOmnichannelAndPricesVisibile,
  )

  const [configurationOptions, setConfigurationOption] = useState([])
  const [configurationInputValue, setConfigurationInputValue] = useState(null)
  const [editIndex, setEditIndex] = useState(0)
  const [activeSaveButton, setActiveSaveButton] = useState(false)
  const [isCreateConfirmVisibile, setIsCreateConfirmVisibile] = useState(false)
  const activeFiltersRef = useRef(activeFilters)
  const configurationRef = useRef(currentConfiguration)

  useEffect(() => {
    if (serverConfig) {
      const configurations = getConfigurationList(
        profileValue,
        publicWorkspace,
        userValue,
        serverConfig,
        profileWorkspaces,
      )

      setConfigurationOption(
        configurations.map(x => {
          return { key: x, value: x }
        }),
      )
    }
  }, [dispatch, publicWorkspace, profileValue, userValue, serverConfig])

  useEffect(() => {
    const previousActiveFilters = activeFiltersRef.current
    const previousConfiguration = configurationRef.current
    setActiveSaveButton(false)
    if (previousActiveFilters !== activeFilters && previousConfiguration === currentConfiguration) {
      activeFiltersRef.current = activeFilters
      setActiveSaveButton(true)
    }
    configurationRef.current = currentConfiguration
  }, [activeFilters, currentConfiguration])

  const hideSectionMenu = useMemo(() => {
    return {
      items: [
        {
          key: 'disable',
          label: 'Disabilita',
          onClick: () => {
            dispatch(setShowConfigurationElement(false))
          },
        },
      ],
    }
  }, [dispatch])

  const closeModal = useCallback(() => {
    setIsCreateConfirmVisibile(false)
    dispatch(setIsConfigurationModalOpen(false))
    setConfigurationInputValue(null)
  }, [dispatch])

  const initEditModal = useCallback(
    (e, value, index) => {
      e.stopPropagation()
      setEditIndex(index)
      setConfigurationInputValue(value)
      dispatch(setIsConfigurationEditModalOpen(true))
    },
    [dispatch],
  )

  const closeEditModal = useCallback(() => {
    setConfigurationInputValue(null)
    setEditIndex(0)
    dispatch(setIsConfigurationEditModalOpen(false))
  }, [dispatch])

  const showNotification = useCallback(message => {
    notification.open({
      description: message,
      message: '',
    })
  }, [])

  const getAndLoadTranslation = useCallback(() => {
    getTranslation()
      .then(data => {
        dispatch(loadLanguage(data))
      })
      .catch(err => {
        handleErrorCatchLog(err, 'Failed loading language translations getTranslation')
      })
  }, [dispatch])

  const onConfigurationChange = useCallback(
    value => {
      dispatch(
        loadConfiguration({
          configurationName: value === publicWorkspace ? publicWorkspace : value,
          defaultProfileOptions: value === publicWorkspace ? defaultProfileOptions : null,
          user: userValue,
          serverConfig: serverConfig,
        }),
      )
      getAndLoadTranslation()
      dispatch(populateTableWithArticles())
      if (isOmnichannelAndPricesVisibile) {
        const typeOfChannel = checkDefaultTypeOfChannel(publicWorkspace).toString()
        dispatch(setTypeOfChannel(parseInt(typeOfChannel)))
        dispatch(setChannelType(parseInt(typeOfChannel)))
      }
    },
    [getAndLoadTranslation, dispatch, defaultProfileOptions, serverConfig, userValue],
  )

  const onInputChange = useCallback(event => {
    setConfigurationInputValue(event.target.value)
  }, [])

  const addConfiguration = useCallback(() => {
    if (!configurationInputValue) {
      showNotification(renderTranslation(language, TranslationKeys.configurationValueNotProvided))
      setIsCreateConfirmVisibile(false)
      return
    }

    const cleanedInputValue = configurationInputValue.replaceAll('_', '')

    if (isFixedWorkspace(cleanedInputValue) || cleanedInputValue.toLowerCase() === publicWorkspace.toLowerCase()) {
      showNotification(renderTranslation(language, TranslationKeys.configurationCreateNewWorkspace))
      return
    }

    const configOptions = [...configurationOptions]
    const optionFound = configOptions.find(({ key, value }) => key === cleanedInputValue)

    if (!optionFound) {
      configOptions.push({ key: cleanedInputValue, value: cleanedInputValue })
      setConfigurationOption(configOptions)
    }

    dispatch(
      saveConfiguration({
        currentConfiguration: cleanedInputValue,
        user: userValue,
        serverConfig: serverConfig,
      }),
    )

    closeModal()
  }, [
    configurationInputValue,
    configurationOptions,
    dispatch,
    serverConfig,
    userValue,
    closeModal,
    showNotification,
    language,
  ])

  const updateConfiguration = useCallback(() => {
    if (!configurationInputValue) {
      showNotification(renderTranslation(language, TranslationKeys.configurationValueNotProvided))
      return
    }

    const configOptions = [...configurationOptions]

    const cleanedInputValue = configurationInputValue.replaceAll('_', '')

    const optionToAdd = { key: cleanedInputValue, value: cleanedInputValue }
    const previousValue = configOptions[editIndex].value
    const optionFound = configOptions.find(({ key, value }) => key === cleanedInputValue)

    if (isFixedWorkspace(cleanedInputValue) || cleanedInputValue.toLowerCase() === publicWorkspace.toLowerCase()) {
      showNotification(renderTranslation(language, TranslationKeys.configurationCreateNewWorkspace))
      return
    }

    if (!optionFound && previousValue !== cleanedInputValue) {
      configOptions.splice(editIndex, 1, optionToAdd)
      setConfigurationOption(configOptions)
      dispatch(
        renameConfiguration({
          before: previousValue,
          after: cleanedInputValue,
          user: userValue,
          serverConfig: serverConfig,
        }),
      )
    } else {
      showNotification(renderTranslation(language, TranslationKeys.configurationDropdownNotification))
    }

    closeEditModal()
  }, [
    configurationInputValue,
    configurationOptions,
    editIndex,
    closeEditModal,
    showNotification,
    language,
    dispatch,
    userValue,
    serverConfig,
  ])

  const removeConfiguration = useCallback(
    index => {
      const configOptions = [...configurationOptions]
      const option = configOptions.find((item, itemIndex) => index === itemIndex)

      if (option && option.value !== publicWorkspace && !isFixedWorkspace(option.value)) {
        configOptions.splice(index, 1)
        setConfigurationOption(configOptions)
        if (currentConfiguration === option.value) {
          dispatch(
            loadConfiguration({
              configurationName: publicWorkspace,
              defaultProfileOptions: defaultProfileOptions,
              user: userValue,
              serverConfig: serverConfig,
            }),
          )
          getAndLoadTranslation()
          dispatch(populateTableWithArticles())
        }
        dispatch(
          deleteConfiguration({
            configurationName: option.value,
            user: userValue,
            serverConfig: serverConfig,
          }),
        )
      }
    },
    [
      configurationOptions,
      currentConfiguration,
      dispatch,
      defaultProfileOptions,
      serverConfig,
      userValue,
      getAndLoadTranslation,
    ],
  )

  const onConfigurationSave = useCallback(() => {
    if (!editTableDisable || isFixedWorkspace(currentConfiguration)) {
      return
    }

    if (currentConfiguration === publicWorkspace) {
      dispatch(setIsConfigurationModalOpen(true))
      return
    }

    dispatch(
      saveConfiguration({
        currentConfiguration: currentConfiguration,
        user: userValue,
        serverConfig: serverConfig,
      }),
    )
    showNotification(renderTranslation(language, TranslationKeys.configurationDropdownSaved))
    setActiveSaveButton(false)
  }, [editTableDisable, currentConfiguration, dispatch, serverConfig, userValue, showNotification, language])

  const handleVisibleChange = useCallback(
    visible => {
      if (!visible) {
        setIsCreateConfirmVisibile(visible)
        return
      }
      const cleanedInputValue = configurationInputValue.replaceAll('_', '')
      const isInputAlreadyInsert = configurationOptions.find(({ key, value }) => key === cleanedInputValue)
      if (isInputAlreadyInsert) {
        setIsCreateConfirmVisibile(visible)
      } else {
        addConfiguration()
      }
    },
    [configurationOptions, addConfiguration, configurationInputValue],
  )

  const confirmOverwrite = useCallback(
    e => {
      stopPropagation(e)
      setIsCreateConfirmVisibile(false)
      setTimeout(addConfiguration, 0)
    },
    [addConfiguration],
  )

  const cancelOverwrite = e => {
    stopPropagation(e)
    setIsCreateConfirmVisibile(false)
  }

  return (
    <>
      <Select
        size='small'
        value={currentConfiguration}
        className='configuration-dropdown'
        onChange={onConfigurationChange}
        optionLabelProp='label' //!important keep option label propertie value for current display selection instead of html
        dropdownRender={menu => (
          <div className='configuration-dropdown-menu'>
            {menu}
            <div key={'create-config-action'} className='configuration-action cursor-pointer'>
              <span
                onClick={() => {
                  dispatch(setIsConfigurationModalOpen(true))
                }}
              >
                {renderTranslation(language, TranslationKeys.channelRangeAdd)}
              </span>
            </div>
          </div>
        )}
        disabled={!editTableDisable}
      >
        {configurationOptions.map(({ key, value }, index) => {
          return (
            <Option key={key} value={key} label={value}>
              <span className='config-value-label truncate-text'>
                <span className='config-name'>{value.length > 25 ? value.substring(0, 25) + '....' : value}</span>
              </span>
              {key !== publicWorkspace && !isFixedWorkspace(key) && (
                <span className='config-icons'>
                  <EditFilled
                    className='config-select-icon edit'
                    onClick={e => {
                      stopPropagation(e)
                      initEditModal(e, value, index)
                    }}
                  />
                  <Popconfirm
                    title={
                      <div>
                        <div>{value + ':'}</div>
                        <div>{renderTranslation(language, TranslationKeys.configurationDropdownPopDesc)}</div>
                      </div>
                    }
                    onConfirm={e => {
                      stopPropagation(e)
                      removeConfiguration(index)
                    }}
                    onCancel={stopPropagation}
                    okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
                    cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
                  >
                    <DeleteFilled onClick={stopPropagation} className='config-select-icon delete' />
                  </Popconfirm>
                </span>
              )}
            </Option>
          )
        })}
      </Select>
      <Modal
        className='config-modal config-modal-create'
        title={renderTranslation(language, TranslationKeys.configurationDropdownCreateModalTitle)}
        width={400}
        footer={[
          <Popconfirm
            key={'popconfirm-create-submit'}
            title={renderTranslation(language, TranslationKeys.configurationPopAlreadyCreateDescription)}
            open={isCreateConfirmVisibile}
            onOpenChange={handleVisibleChange}
            onConfirm={confirmOverwrite}
            onCancel={cancelOverwrite}
            okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
            cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
          >
            <Button
              onClick={stopPropagation}
              key='create-submit'
              type='primary'
              className='configuration-modal-button standard-btn'
            >
              {renderTranslation(language, TranslationKeys.configurationDropdownCreateModalButton)}
            </Button>
          </Popconfirm>,
        ]}
        open={isConfigurationModalOpen}
        onCancel={closeModal}
      >
        <Input
          placeholder={renderTranslation(language, TranslationKeys.configurationDropdownModalInputPlaceholder)}
          value={configurationInputValue}
          onChange={onInputChange}
          allowClear={{ clearIcon: <CloseOutlined className='general-icon input-clear-icon' /> }}
        />
      </Modal>
      <Modal
        className='config-modal config-modal-update'
        title={renderTranslation(language, TranslationKeys.configurationDropdownEditModalTitle)}
        width={400}
        footer={[
          <Button
            key='edit-submit'
            type='primary'
            className='configuration-modal-button standard-btn'
            onClick={updateConfiguration}
          >
            {renderTranslation(language, TranslationKeys.configurationDropdownEditModalButton)}
          </Button>,
        ]}
        open={isConfigurationEditModalOpen}
        onCancel={closeEditModal}
      >
        <Input
          placeholder={renderTranslation(language, TranslationKeys.configurationDropdownModalInputPlaceholder)}
          value={configurationInputValue}
          onChange={onInputChange}
          allowClear={{ clearIcon: <CloseOutlined className='general-icon input-clear-icon' /> }}
        />
      </Modal>
      <Tooltip
        open={isFixedWorkspace(currentConfiguration) ? undefined : false}
        title={renderTranslation(language, TranslationKeys.configurationCreateNewWorkspace)}
        placement='bottom'
        trigger='hover'
      >
        <span className='icon-block'>
          <SaveFilled
            id='saveWorkspaceIcon'
            className={
              'generic-icon save-icon' +
              (!editTableDisable || isFixedWorkspace(currentConfiguration)
                ? ' d-none'
                : activeSaveButton
                ? ' active'
                : '')
            }
            onClick={onConfigurationSave}
          />
        </span>
      </Tooltip>
    </>
  )
}

export default ConfigurationDropdown
